import api from '../services/api';

export type CrmCampaignCategory =
  | 'LEAD'
  | 'RETENTION'
  | 'BIRTHDAY'
  | 'TREATMENT_RETURN'
  | 'OFFICE'
  | 'OTHERS';

export type CrmCampaignStatus =
  | 'DRAFT'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED';

export type CrmScheduleType = 'NOW' | 'SCHEDULED';
export type CrmMessageType = 'TEXT' | 'TEMPLATE';

export interface ICrmCampaign {
  id: string;
  name: string;
  description?: string;
  category: CrmCampaignCategory;
  status: CrmCampaignStatus;
  audience_filters?: Record<string, unknown> | null;
  schedule_type: CrmScheduleType;
  scheduled_at?: string | null;
  message_type: CrmMessageType;
  whatsapp_template_id?: string | null;
  message_body?: string | null;
  total_target: number;
  sent_count: number;
  delivered_count: number;
  read_count: number;
  failed_count: number;
  scheduler_processed?: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export type CrmRecipientStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED';

export interface ICrmRecipient {
  id: string;
  campaign_id: string;
  client_id?: string | null;
  client_name?: string | null;
  phone: string;
  status: CrmRecipientStatus;
  error_message?: string | null;
  wa_message_id?: string | null;
  retry_count: number;
  rendered_payload?: Record<string, unknown> | null;
  message_sent_at?: string | null;
  delivered_at?: string | null;
  read_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface ICrmTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  body_text: string;
  placeholders?: string[] | Record<string, unknown> | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
}

export type CrmMessageDirection = 'INBOUND' | 'OUTBOUND';
export type CrmMessageSource = 'MANUAL' | 'CAMPAIGN' | 'AUTOMATION';
export type CrmMessageLogStatus =
  | 'PENDING'
  | 'QUEUED'
  | 'SENT'
  | 'DELIVERED'
  | 'READ'
  | 'FAILED'
  | 'RECEIVED';

export interface ICrmMessageLog {
  id: string;
  direction: CrmMessageDirection;
  recipient_phone: string;
  client_id?: string | null;
  client_name?: string | null;
  operator_user_id?: string | null;
  campaign_id?: string | null;
  message_type: string;
  content: string;
  wa_message_id?: string | null;
  status: CrmMessageLogStatus;
  source: CrmMessageSource;
  created_at: string;
}

export interface AudienceClient {
  id: string;
  code: number;
  corporate_name: string;
  cell_phone: string;
  email?: string | null;
  birthday: string;
  gender?: string | null;
  covenant?: string | null;
}

export interface IResponseAudience {
  count: number;
  clients: AudienceClient[];
  filters_applied: Record<string, unknown>;
}

interface IResponseGet<T> {
  data: T[];
  count: number;
}

export const getCrmCampaigns = async (
  limit = 10,
  offset = 0,
  search = '',
  category?: CrmCampaignCategory,
  status?: CrmCampaignStatus,
): Promise<IResponseGet<ICrmCampaign>> => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);
  if (category) params.set('category', category);
  if (status) params.set('status', status);
  const { data } = await api.get(`/crm/campaigns?${params.toString()}`);
  return data;
};

export const getCrmCampaignById = async (
  id: string,
): Promise<ICrmCampaign | null> => {
  const { data } = await api.get(`/crm/campaigns/${id}`);
  return data;
};

export const postCrmCampaign = async (
  payload: Partial<ICrmCampaign> | Record<string, unknown>,
): Promise<ICrmCampaign> => {
  const { data } = await api.post('/crm/campaigns', payload);
  return data;
};

export const patchCrmCampaign = async (
  id: string,
  payload: Record<string, unknown>,
): Promise<ICrmCampaign> => {
  const { data } = await api.patch(`/crm/campaigns/${id}`, payload);
  return data;
};

export const deleteCrmCampaign = async (id: string): Promise<void> => {
  await api.delete(`/crm/campaigns/${id}`);
};

export const calculateCrmAudience = async (
  filters: Record<string, unknown>,
  category?: CrmCampaignCategory,
): Promise<IResponseAudience> => {
  const params = new URLSearchParams();
  if (category) params.set('category', category);
  if (filters) params.set('filters', encodeURIComponent(JSON.stringify(filters)));
  const { data } = await api.get(
    `/crm/campaigns/audience/calculate?${params.toString()}`,
  );
  return data;
};

export const startCrmCampaign = async (
  id: string,
  payload: { force_reset_recipients?: boolean } = {},
): Promise<{ campaign_id: string; queued: number; skipped: number }> => {
  const { data } = await api.post(`/crm/campaigns/${id}/start`, payload);
  return data;
};

export const pauseCrmCampaign = async (id: string): Promise<ICrmCampaign> => {
  const { data } = await api.post(`/crm/campaigns/${id}/pause`);
  return data;
};

export const resumeCrmCampaign = async (id: string): Promise<ICrmCampaign> => {
  const { data } = await api.post(`/crm/campaigns/${id}/resume`);
  return data;
};

export const getCrmCampaignRecipients = async (
  campaignId: string,
  limit = 50,
  offset = 0,
  search = '',
  status?: CrmRecipientStatus,
): Promise<IResponseGet<ICrmRecipient>> => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);
  if (status) params.set('status', status);
  const { data } = await api.get(
    `/crm/campaigns/${campaignId}/recipients?${params.toString()}`,
  );
  return data;
};

export const retryCrmRecipient = async (
  campaignId: string,
  recipientId: string,
): Promise<ICrmRecipient> => {
  const { data } = await api.post(
    `/crm/campaigns/${campaignId}/recipients/${recipientId}/retry`,
  );
  return data;
};

export const retryAllFailedCrmRecipients = async (
  campaignId: string,
  only_failed = true,
): Promise<{ retried: number; total: number }> => {
  const { data } = await api.post(
    `/crm/campaigns/${campaignId}/recipients/retry/failed`,
    { only_failed },
  );
  return data;
};

export const getCrmTemplates = async (
  limit = 50,
  offset = 0,
  search = '',
  category?: string,
  is_active?: boolean,
): Promise<IResponseGet<ICrmTemplate>> => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);
  if (category) params.set('category', category);
  if (typeof is_active === 'boolean') params.set('is_active', String(is_active));
  const { data } = await api.get(`/crm/templates?${params.toString()}`);
  return data;
};

export const getCrmTemplateById = async (
  id: string,
): Promise<ICrmTemplate | null> => {
  const { data } = await api.get(`/crm/templates/${id}`);
  return data;
};

export const postCrmTemplate = async (
  payload: Record<string, unknown>,
): Promise<ICrmTemplate> => {
  const { data } = await api.post('/crm/templates', payload);
  return data;
};

export const patchCrmTemplate = async (
  id: string,
  payload: Record<string, unknown>,
): Promise<ICrmTemplate> => {
  const { data } = await api.patch(`/crm/templates/${id}`, payload);
  return data;
};

export const deleteCrmTemplate = async (id: string): Promise<void> => {
  await api.delete(`/crm/templates/${id}`);
};

export const previewCrmTemplate = async (payload: Record<string, unknown>): Promise<{
  rendered: string;
  placeholders: string[];
  client_preview: Record<string, unknown> | null;
}> => {
  const { data } = await api.post('/crm/templates/preview', payload);
  return data;
};

export const sendManualCrmMessage = async (
  payload: Record<string, unknown>,
): Promise<{ ok: boolean; queued: boolean; phone: string; client_name?: string }> => {
  const { data } = await api.post('/crm/messages/send', payload);
  return data;
};

export const bulkSendManualCrmMessage = async (
  payload: Record<string, unknown>,
): Promise<{ queued: number; skipped: number }> => {
  const { data } = await api.post('/crm/messages/bulk-send', payload);
  return data;
};

export const getCrmInboxMessages = async (
  limit = 50,
  offset = 0,
  search = '',
  client_id?: string,
  direction?: CrmMessageDirection,
  source?: CrmMessageSource,
  status?: CrmMessageLogStatus,
): Promise<IResponseGet<ICrmMessageLog>> => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  params.set('offset', String(offset));
  if (search) params.set('search', search);
  if (client_id) params.set('client_id', client_id);
  if (direction) params.set('direction', direction);
  if (source) params.set('source', source);
  if (status) params.set('status', status);
  const { data } = await api.get(`/crm/messages/inbox?${params.toString()}`);
  return data;
};
